/**
 * Chat popup component for discussing articles and topics.
 *
 * A floating chat widget similar to customer support chat boxes that allows
 * users to ask questions about article content with AI-powered responses.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { sendChatMessage, createChatSession, clearChatSession } from '../services/chat';
import { useToast } from '../hooks/useToast';

/**
 * ChatPopup - Floating chat widget for article/topic discussions
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the popup is open
 * @param {function} props.onClose - Callback when popup is closed
 * @param {string} props.contextType - Either "article" or "topic"
 * @param {string|number} props.contextId - The article or topic ID
 * @param {string} props.contextTitle - Title to display in header
 */
const ChatPopup = ({
  isOpen,
  onClose,
  contextType,
  contextId,
  contextTitle
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { showError } = useToast();

  // Initialize session when popup opens
  useEffect(() => {
    if (isOpen && !isInitialized) {
      initializeChat();
    }
  }, [isOpen, isInitialized]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && isInitialized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isInitialized]);

  const initializeChat = async () => {
    try {
      const response = await createChatSession();
      setSessionId(response.session_id);
      setMessages([{
        id: Date.now(),
        type: 'system',
        content: `Hi! I'm here to help you understand this ${contextType}. Ask me anything!`,
        timestamp: new Date()
      }]);
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      showError('Failed to start chat. Please try again.');
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();

    const message = inputValue.trim();
    if (!message || isLoading) return;

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await sendChatMessage(
        contextType,
        contextId,
        message,
        sessionId
      );

      // Update session ID if new
      if (response.session_id !== sessionId) {
        setSessionId(response.session_id);
      }

      // Add AI response
      const aiMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response.response,
        citations: response.citations || [],
        grounded: response.grounded,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: error.response?.data?.detail || 'Failed to get response. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      showError('Chat failed. Please try again.');
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleNewChat = async () => {
    if (sessionId) {
      try {
        await clearChatSession(sessionId);
      } catch {
        // Ignore clear errors
      }
    }
    setMessages([]);
    setSessionId(null);
    setIsInitialized(false);
    initializeChat();
  };

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  // Size configurations
  const sizeClasses = isExpanded
    ? 'w-[600px] max-w-[calc(100vw-3rem)]'
    : 'w-96 max-w-[calc(100vw-3rem)]';

  const maxHeightStyle = isExpanded
    ? { maxHeight: 'calc(100vh - 8rem)', minHeight: '500px' }
    : { maxHeight: 'calc(100vh - 10rem)' };

  return (
    <div
      className={`fixed bottom-24 right-6 ${sizeClasses} bg-surface rounded-lg shadow-2xl border border-theme-border-subtle flex flex-col z-50 overflow-hidden transition-all duration-300`}
      style={maxHeightStyle}
    >
      {/* Header */}
      <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-2 min-w-0">
          <span className="text-lg">💬</span>
          <div className="min-w-0">
            <h3 className="font-medium text-sm truncate">
              Chat about: {contextTitle || contextType}
            </h3>
          </div>
        </div>
        <div className="flex items-center space-x-1 flex-shrink-0">
          <button
            onClick={handleNewChat}
            className="p-1.5 hover:bg-blue-500 rounded transition-colors"
            title="New chat"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 hover:bg-blue-500 rounded transition-colors"
            title={isExpanded ? 'Shrink chat' : 'Expand chat'}
          >
            {isExpanded ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              </svg>
            )}
          </button>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-blue-500 rounded transition-colors"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-page-bg">
        {messages.map((message) => (
          <div key={message.id} className="flex flex-col">
            <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-lg shadow-sm ${
                message.type === 'user'
                  ? 'bg-blue-500 text-white'
                  : message.type === 'error'
                  ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
                  : 'bg-surface text-heading border border-theme-border-subtle'
              }`}>
                {message.type === 'user' ? (
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                ) : (
                  <div className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-2 prose-pre:my-2 prose-pre:bg-surface-hover prose-pre:text-heading prose-code:text-pink-600 prose-code:bg-surface-hover prose-code:px-1 prose-code:rounded">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                )}

                {/* Citations */}
                {message.citations && message.citations.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-theme-border-subtle">
                    <p className="text-xs text-secondary-text mb-1">Sources:</p>
                    <ul className="space-y-1">
                      {message.citations.map((citation, idx) => (
                        <li key={idx}>
                          <a
                            href={citation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center"
                          >
                            <span className="mr-1">🔗</span>
                            <span className="truncate">{citation.title}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className={`text-xs mt-1 ${
                  message.type === 'user' ? 'text-blue-100' : 'text-tertiary-text'
                }`}>
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-surface px-4 py-3 rounded-lg shadow-sm border border-theme-border-subtle">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-tertiary-text rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-tertiary-text rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-tertiary-text rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <span className="text-sm text-secondary-text">Thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t bg-surface p-3 flex-shrink-0">
        <form onSubmit={handleSend} className="flex space-x-2 items-end">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              // Auto-resize textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={(e) => {
              // Submit on Enter, allow new line on Shift+Enter
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            placeholder="Ask a question..."
            className="flex-1 px-3 py-2 border border-theme-border-subtle rounded-lg bg-surface text-body-text placeholder-[color:var(--color-text-tertiary)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none overflow-hidden"
            disabled={isLoading || !isInitialized}
            rows={1}
            style={{ minHeight: '38px', maxHeight: '120px' }}
          />
          <button
            type="submit"
            disabled={isLoading || !inputValue.trim() || !isInitialized}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex-shrink-0"
          >
            {isLoading ? '...' : 'Send'}
          </button>
        </form>
        <p className="text-xs text-tertiary-text mt-2 text-center">
          Shift+Enter for new line • Powered by Google Search
        </p>
      </div>
    </div>
  );
};

/**
 * ChatFAB - Floating Action Button to open chat
 *
 * @param {Object} props
 * @param {function} props.onClick - Callback when button is clicked
 * @param {boolean} props.isOpen - Whether chat is currently open
 */
export const ChatFAB = ({ onClick, isOpen }) => {
  return (
    <button
      onClick={onClick}
      className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 z-40 ${
        isOpen
          ? 'bg-gray-600 hover:bg-gray-700'
          : 'bg-blue-600 hover:bg-blue-700 hover:scale-105'
      }`}
      title={isOpen ? 'Close chat' : 'Chat about this content'}
    >
      {isOpen ? (
        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      ) : (
        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      )}
    </button>
  );
};

export default ChatPopup;
